import React, { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { AssigneeDirectory } from './actionEditModel';
import {
  actionsForHandover,
  handoverPeople,
  handoverPositions,
} from './actionBulkModel';
import type { ActionEntity } from '../hooks/useScopedActionForm';

type Props = {
  show: boolean;
  onClose: () => void;
  actions: ActionEntity[];
  directory: AssigneeDirectory;
  onConfirm: (positionId: string, userId: string) => void;
  isSaving?: boolean;
};

/**
 * Reassign a whole ICS position's open work to one incoming person.
 *
 * The highest-value bulk case: a relief shows up and the outgoing OSC's open
 * tasks have to move in one operation rather than one cell at a time.
 */
export const ShiftHandoverDialog: React.FC<Props> = ({
  show,
  onClose,
  actions,
  directory,
  onConfirm,
  isSaving = false,
}) => {
  const positions = useMemo(
    () => handoverPositions(actions, directory),
    [actions, directory]
  );
  const people = useMemo(() => handoverPeople(directory), [directory]);
  const [positionId, setPositionId] = useState('');
  const [userId, setUserId] = useState('');

  const preview = useMemo(
    () => (positionId ? actionsForHandover(actions, positionId) : []),
    [actions, positionId]
  );

  const selectedPosition = positions.find(
    (position) => position.id === positionId
  );
  const selectedPerson = people.find((person) => person.id === userId);

  return (
    <Dialog
      open={show}
      onOpenChange={(next) => {
        if (!next) onClose();
      }}
    >
      <DialogContent className="dark">
        <DialogHeader>
          <DialogTitle>Shift handover</DialogTitle>
          <DialogDescription>
            Move every open task assigned to an ICS position onto the incoming
            person. Closed work stays where it is.
          </DialogDescription>
        </DialogHeader>
        <Label htmlFor="handover-position">Position</Label>
        <Select
          value={positionId || null}
          onValueChange={(next) => setPositionId(String(next ?? ''))}
        >
          <SelectTrigger id="handover-position" className="tw:w-full">
            <SelectValue placeholder="Select a position">
              {selectedPosition
                ? `${selectedPosition.label} (${selectedPosition.count})`
                : 'Select a position'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="dark">
            {positions.map((position) => (
              <SelectItem key={position.id} value={position.id}>
                {position.label} ({position.count})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Label htmlFor="handover-person">Incoming person</Label>
        <Select
          value={userId || null}
          onValueChange={(next) => setUserId(String(next ?? ''))}
        >
          <SelectTrigger id="handover-person" className="tw:w-full">
            <SelectValue placeholder="Select a person">
              {selectedPerson?.label ?? 'Select a person'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent className="dark">
            {people.map((person) => (
              <SelectItem key={person.id} value={person.id}>
                {person.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {preview.length > 0 ? (
          <p className="tw:mb-0 tw:text-sm">
            {preview.length === 1
              ? '1 open task will move.'
              : `${preview.length} open tasks will move.`}
          </p>
        ) : null}
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            type="button"
            disabled={!positionId || !userId || isSaving}
            onClick={() => onConfirm(positionId, userId)}
          >
            Hand over
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default ShiftHandoverDialog;
